import React from 'react';
import { useFormik } from 'formik';
import { Form } from '@themesberg/react-bootstrap';
import { END_POINT_PO_SALES } from '../const';
import usePoPaymentTerms from '../hooks/usePoPaymentTerms';
import PoSalesFormFields, { poSalesFormSchema, poSalesPayload } from '../components/PoSalesFormFields';
import useRecordEditor from '../hooks/useRecordEditor';
import RecordEditFeedback from '../components/RecordEditFeedback';
import RecordEditActions from '../components/RecordEditActions';
import { buildQuoteProducts } from '../utils/quoteProducts';

export default function PoSaleEditForm({ poSaleId, onSuccess, onCancel, onSubmittingChange }) {
  const terms = usePoPaymentTerms();
  const editor = useRecordEditor({
    recordId: poSaleId, endpoint: END_POINT_PO_SALES, label: 'PO Sales',
    onSuccess, onSubmittingChange,
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      products: buildQuoteProducts({ products: editor.record?.products }),
      // One blank row by default; a fixed key keeps these initial values stable across renders.
      invoices: editor.record?.invoices?.length ? editor.record.invoices.map(({ id, invoice_no, invoice_date, transporter_name, reference_number }) => ({
        id, invoice_no: invoice_no ?? '', invoice_date: String(invoice_date ?? '').slice(0, 10),
        transporter_name: transporter_name ?? '', reference_number: reference_number ?? '',
      })) : [{ _key: 'default-invoice', invoice_no: '', invoice_date: '', transporter_name: '', reference_number: '' }],
      po_number: editor.record?.po_number ?? '',
      po_release_date: String(editor.record?.po_release_date ?? '').slice(0, 10),
      po_received_date: String(editor.record?.po_received_date ?? '').slice(0, 10),
      delivery_due_date: String(editor.record?.delivery_due_date ?? '').slice(0, 10),
      po_to_principal_vendor: editor.record?.po_to_principal_vendor ?? 'No',
      stock_reference_number: editor.record?.stock_reference_number ?? '',
      stock_reference_date: String(editor.record?.stock_reference_date ?? '').slice(0, 10),
      principal_reference_number: editor.record?.principal_reference_number ?? '',
      principal_reference_date: String(editor.record?.principal_reference_date ?? '').slice(0, 10),
      pi: editor.record?.pi ?? 'No',
      pi_reference_number: editor.record?.pi_reference_number ?? '',
      pi_reference_date: String(editor.record?.pi_reference_date ?? '').slice(0, 10),
      payment_term: editor.record?.payment_term ?? '',
      status: editor.record?.status ?? 'Pending',
      remarks: editor.record?.remarks ?? '',
    },
    validationSchema: poSalesFormSchema(terms.data, editor.record?.payment_term),
    onSubmit: (values, helpers) => {
      if (terms.loading || terms.error) { helpers.setSubmitting(false); return; }
      return editor.submit(poSalesPayload(values), helpers);
    },
  });

  if (editor.loading || editor.loadError || !editor.record) {
    return <RecordEditFeedback loading={editor.loading} error={editor.loadError}
      label="PO Sales" onRetry={editor.retry} onCancel={onCancel} />;
  }

  const nested = editor.record.quote || {};
  const quote = {
    ...nested,
    system_quote_no: editor.record.system_quote_no || nested.system_quote_no,
    offer_no: editor.record.offer_no || nested.offer_no,
    offer_date: nested.offer_date || editor.record.offer_date,
    expected_date: nested.expected_date || editor.record.expected_date,
  };

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      <PoSalesFormFields formik={formik} quote={quote} terms={terms} classificationInProducts invoiceSection poNumberReadOnly editDetails
        actions={<RecordEditActions formik={formik} backTo="/po-sales" onCancel={onCancel}
          saveDisabled={terms.loading || Boolean(terms.error)} />} />
    </Form>
  );
}
